import { normalizeMentionKind } from "#app/components/editor/mention-refs";
import { at } from "#app/lib/utils/at";

// Pulls @-mention USER ids out of TipTap-generated note HTML. Mentions render
// as a span carrying data-type + data-id (see components/editor/mention-refs.ts
// for the serialization contract). EVERY entity kind — user, team, issue,
// stage, workflow, issueType — serializes a data-id, so a bare data-id scan
// over-collects non-user ids and then treats them as mentioned users. We read
// each span's data-type and keep only person mentions: normalizeMentionKind
// maps data-type "user" / legacy "mention" / (absent, the lenient legacy
// person shape) to "user", and every other kind to itself. Returns a
// deduplicated list preserving first-seen order.
export function extractMentionedUserIds(html: string | null | undefined): string[] {
  if (!html) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  // Match each span opening tag, then pull data-id + data-type from its
  // attributes (order-independent). DOMParser would be cleaner but pulls in a
  // browser-only API on test/server paths; the mention markup is generated by
  // TipTap and stable, so the attribute-anchored regex is safe.
  const tagRe = /<span\b([^>]*)>/gi;
  let m: RegExpExecArray | null;
  while ((m = tagRe.exec(html)) !== null) {
    // Group 1 is not optional, so a match always carries the attribute run.
    const attrs = at(m, 1, "span attributes");
    const idMatch = /\bdata-id="([^"]+)"/i.exec(attrs);
    if (!idMatch) continue;
    const id = idMatch[1];
    if (!id || seen.has(id)) continue;
    const typeMatch = /\bdata-type="([^"]*)"/i.exec(attrs);
    // Skip team/issue/stage/workflow/issueType mentions — only person
    // mentions are mentioned-user ids.
    if (normalizeMentionKind(typeMatch ? at(typeMatch, 1, "data-type") : null) !== "user") {
      continue;
    }
    seen.add(id);
    out.push(id);
  }
  return out;
}
